import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  TableContainer,
  Table,
  TableBody,
  TableRow,
  TableCell,
  TextField,
  Snackbar,
  Chip,
} from '@mui/material';
import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { APPLICATION_ID, ROUTE_APPLICATION, ROUTE_APPLICATION_EDIT } from '../../constants';
import { useNavigate } from 'react-router-dom';
import IconButtonBox from '../../components/IconButtonBox';
import DialogBox from '../../components/DialogBox';
import useApplicationStore from '../../stores/ApplicationStore';
import { ContentCopy } from '@mui/icons-material';
import { openDialog, closeDialog, getLocalStorage, useError } from '../../utils/functions';
import OutlinedButton from '../../components/OutlinedButton';
import ApplicationApi from '../../api/ApplicationApi';
import { RedirectURI } from '../../types/ApplicationType';

const ApplicationInformation: React.FC = () => {
  const { setError } = useError();
  const navigate = useNavigate();
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const { setCreateMode, name, description, website, redirectUri, setName, setDescription, setWebsite, setRedirectUri, setSelectedOptions } = useApplicationStore();
  const [dialogTitle, setDialogTitle] = useState('');
  const [dialogContent, setDialogContent] = useState<React.ReactNode>(null);
  const [appObj, setAppObj] = useState<any>(null);
  const [apiKeyObj, setApiKeyObj] = useState<any>(null);
  const [showSelect, setShowSelect] = useState<string[]>([]);

  const [formValues, setFormValues] = useState({
    identity: '',
    secret: '',
    clientId: '',
    apiKey: '',
  });

  const setValue = (field: string, value: string) => {
    setFormValues((prevValues) => ({ ...prevValues, [field]: value }));
  };

  const handleDelete = () => {
    if (dialogTitle === 'Delete') {
      doDelete();
    } else if (dialogTitle === 'Revoke') {
      doRevoke();
    } else if (dialogTitle === 'Renew') {
      doRenew();
    }

    closeDialog(setIsDialogOpen);
  };

  const handleEdit = () => {
    setCreateMode(false);
    navigate(ROUTE_APPLICATION_EDIT);
  }

  const handleDailogDelete = (action: string) => {
    setDialogTitle(action);

    if (action === 'Delete') {
      setDialogContent(
        <>
          <Typography>
            {name} will be permanently deleted
          </Typography>
        </>
      );
    } else if (action === 'Revoke') {
      setDialogContent(
        <>
          <Typography>
            The API key will be deleted immediately and permanently.<br />
            Once deleted, it can no longer be used to make API requests.
          </Typography>
        </>
      );
    } else if (action === 'Renew') {
      setDialogContent(
        <>
          <Typography>
            The API key will be deleted immediately and permanently.<br />
            Once deleted, it can no longer be used to make API requests.
          </Typography>
        </>
      );
    }


    openDialog(setIsDialogOpen);
  }

  const doDelete = async () => {
    if (appObj) {
      await ApplicationApi.deleteApp(appObj.id);
      if (appObj.redirectUri) {
        for (let redirect of appObj.redirectUri) {
          await ApplicationApi.deleteRedirectUri(appObj.id, redirect.id);
        }
      }

      if (appObj.projects) {
        for (let project of appObj.projects) {
          await ApplicationApi.deleteProjectAccess(appObj.id, project.id);
        }
      }

      navigate(ROUTE_APPLICATION);
    }
  };

  const doRevoke = async () => {
    if (appObj && apiKeyObj) {
      await ApplicationApi.revokeApiKey(appObj.id, apiKeyObj.id);
      setApiKeyObj(null);
      setValue('apiKey', '');
    }
  };

  const doRenew = async () => {
    if (appObj) {
      doRevoke();

      const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));
      await sleep(500); // 延遲 500 毫秒

      const res = await ApplicationApi.createApiKey(appObj.id);

      if (res.code) {
        setError(res.message);
      } else {
        setValue('apiKey', res.data.token);
        setApiKeyObj(res.data);
      }
    }
  };

  const copyToClipboard = (text: string) => {
    if (!text) return;
    const textArea = document.createElement('textarea');
    textArea.value = text;
    document.body.appendChild(textArea);
    textArea.select();
    try {
      document.execCommand('copy');
      setOpenSnackbar(true);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
    document.body.removeChild(textArea);
  };

  const queryApplication = async () => {
    try {
      const appsResponse = await ApplicationApi.getApp(getLocalStorage(APPLICATION_ID));

      if (appsResponse && appsResponse.data) {
        const data = appsResponse.data;
        setAppObj(data);
        setValue('identity', data.id || '');
        setValue('secret', data.secret || '');
        setValue('clientId', `app:${data.id}` || '');
        setValue('apiKey', data.apiKey || '');
        setName(data.name || '');
        setDescription(data.description || '');
        setWebsite(data.website || '');

        if (data.redirectURIs) {
          let uris = [] as RedirectURI[];
          for (let redirect of data.redirectURIs) {
            uris.push({ id: redirect.id, redirectURI: redirect.redirectURI });
          }

          setRedirectUri(uris);
        }

        if (data.projects) {
          let pro = [] as string[];
          let show = [] as string[];
          for (let project of data.projects) {
            pro.push(project.id);
            show.push(project.name);
          }

          setSelectedOptions(pro);
          setShowSelect(show);
        }

      } else if (appsResponse.code) {
        setError(appsResponse.message);
      }

      const apiKeysResponse = await ApplicationApi.getApiKeys(getLocalStorage(APPLICATION_ID));
      if (apiKeysResponse && apiKeysResponse.data && apiKeysResponse.data.length > 0) {
        setValue('apiKey', apiKeysResponse.data[0].token);
        setApiKeyObj(apiKeysResponse.data[0]);
      } else if (apiKeysResponse.code) {
        setError(apiKeysResponse.message);
      }
    } catch (error) {
      console.error("Error fetching appsResponse data:", error);
    }
  }

  useEffect(() => {
    queryApplication();
  }, []);

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ mb: 3 }}>
        <IconButtonBox
          icon={<KeyboardBackspaceIcon />}
          text='Back to application list'
          onClick={() => navigate(ROUTE_APPLICATION)} />
      </Box>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Box mb={4}>
          <Box display="flex" gap={2} alignItems="center">
            <Typography variant="h4" gutterBottom className='primaryBlueColor'>{name}</Typography>
          </Box>
        </Box>
        <Box>
          <OutlinedButton
            onClick={() => handleDailogDelete('Delete')}
            icon={<DeleteOutlineOutlinedIcon />}
            text='Delete' />
          <OutlinedButton
            onClick={handleEdit}
            icon={<EditOutlinedIcon />}
            text='Edit' />
        </Box>
      </Box>
      <Paper sx={{ padding: 2 }}>
        <Box mb={4} display="flex" gap={4}>
          <Box flex={1}>
            <Typography className='text_gray'>ID/SECRET</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', mb: 2 }}>
              <Typography className='w100'>Identity</Typography>
              <TextField
                variant="outlined"
                value={formValues.identity}
                size="small"
                disabled={true}
                className='w400'
              />
              <ContentCopy fontSize="small" onClick={() => copyToClipboard(formValues.identity)} />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', mb: 2 }}>
              <Typography className='w100'>Secret</Typography>
              <TextField
                variant="outlined"
                value={formValues.secret}
                size="small"
                disabled={true}
                className='w400'
              />
              <ContentCopy fontSize="small" onClick={() => copyToClipboard(formValues.secret)} />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', mb: 2 }}>
              <Typography className='w100'>Client ID</Typography>
              <TextField
                variant="outlined"
                value={formValues.clientId}
                size="small"
                disabled={true}
                className='w400'
              />
              <ContentCopy fontSize="small" onClick={() => copyToClipboard(formValues.clientId)} />
            </Box>
          </Box>

          <Box flex={1}>
            <Typography className='text_gray'>API KEY</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', mb: 2 }}>
              <Typography className='w100'>API key</Typography>
              <TextField
                variant="outlined"
                value={formValues.apiKey}
                size="small"
                disabled={true}
                className='w400'
              />
              <ContentCopy fontSize="small" onClick={() => copyToClipboard(formValues.apiKey)} />
              <OutlinedButton
                onClick={() => handleDailogDelete('Revoke')}
                text='Revoke' />
              <OutlinedButton
                onClick={() => handleDailogDelete('Renew')}
                text='Renew' />
            </Box>
          </Box>
        </Box>
      </Paper>
      <Paper sx={{ padding: 2 }}>
        <Typography variant="h6" gutterBottom>
          Information
        </Typography>
        <TableContainer>
          <Table>
            <TableBody>
              <TableRow>
                <TableCell><strong>Name</strong></TableCell>
                <TableCell>{name}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell><strong>Description</strong></TableCell>
                <TableCell>{description}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell><strong>Website</strong></TableCell>
                <TableCell>{website}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell><strong>Redirect URI</strong></TableCell>
                <TableCell>
                  {redirectUri.map((uri, index) => (
                    <TableCell key={uri.id}>{uri.redirectURI}</TableCell>
                  ))}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  <strong>Connected Project</strong>
                </TableCell>
                <TableCell>
                  {showSelect.length > 0 ? (
                    <>
                      {showSelect.slice(0, 4).map((option, index) => (
                        <Chip
                          key={option}
                          label={option}
                          sx={{ marginRight: "5px", backgroundColor: "#E8EAF6", color: "#1A237E" }}
                        />
                      ))}
                      {showSelect.length > 4 && (
                        <Typography
                          component="span"
                          sx={{ color: "#1A237E", cursor: "pointer", marginLeft: "5px" }}
                          onClick={() => console.log("Show more projects")}
                        >
                          ...See more
                        </Typography>
                      )}
                    </>
                  ) : (
                    <Typography component="span" sx={{ color: "gray" }}>
                      No project selected
                    </Typography>
                  )}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      <DialogBox
        open={isDialogOpen}
        title={dialogTitle}
        dialogContent={dialogContent}
        onCancel={() => closeDialog(setIsDialogOpen)}
        onConfirm={handleDelete}
        confirmLabel={dialogTitle}
        isError={dialogTitle !== "Renew"}
      />
      <Snackbar
        open={openSnackbar}
        autoHideDuration={2000}
        message="Copied!"
        onClose={() => setOpenSnackbar(false)}
      />
    </Box>
  );
};

export default ApplicationInformation;
